import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import {
  CheckCircle,
  Check,
  CreditCard,
  Loader2,
  TrendingUp,
  Star,
  Users,
  BarChart,
  Shield,
  Headphones,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

const benefits = [
  { icon: CheckCircle, text: 'Verified checkmark badge across platform' },
  { icon: TrendingUp, text: 'Priority search ranking algorithm boost' },
  { icon: Star, text: 'Featured in curated editorial playlists' },
  { icon: BarChart, text: 'Lower platform fees (10% vs 15%)' },
  { icon: Users, text: 'Monthly analytics dashboard' },
  { icon: Headphones, text: 'Dedicated support channel' },
];

const Verification = () => {
  const { user, token, isAuthenticated, isVerified } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }

    setProcessing(true);
    try {
      const orderRes = await axios.post(
        `${API}/payments/create-order`,
        {
          amount: 79900,
          currency: 'INR',
          item_type: 'subscription',
          item_id: user.id,
          notes: {
            subscription_type: 'verification',
            user_id: user.id,
            user_email: user.email,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const options = {
        key: RAZORPAY_KEY,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: 'M4manna',
        description: 'Artist Verification Subscription - Monthly',
        order_id: orderRes.data.order_id,
        handler: async (response) => {
          try {
            await axios.post(
              `${API}/payments/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Verification activated!');
            window.location.href = '/dashboard';
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: { name: user.full_name, email: user.email },
        theme: { color: '#FFE500' },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to process subscription');
    } finally {
      setProcessing(false);
    }
  };

  if (isVerified) {
    return (
      <Layout>
        <div className="py-16 px-4 md:px-8">
          <div className="container mx-auto max-w-2xl text-center">
            <div className="h-24 w-24 rounded-full bg-[#FFE500] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-[#FFE500]/30">
              <CheckCircle className="h-12 w-12 text-black" />
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4">You're Verified!</h1>
            <p className="text-[#A0A0A0] mb-8">
              You're already enjoying all the benefits of a verified artist account.
            </p>
            <button onClick={() => navigate('/dashboard')} className="pill-btn pill-btn-active">
              Go to Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 px-4 md:px-8 pb-40">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#FFE500]/10 border border-[#FFE500]/20 px-4 py-2 rounded-full mb-6">
              <Shield className="h-4 w-4 text-[#FFE500]" />
              <span className="text-sm text-[#FFE500] font-semibold uppercase tracking-wider">
                Artist Verification
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4">
              Get <span className="text-[#FFE500]">Verified</span>
            </h1>
            <p className="text-lg text-[#A0A0A0] max-w-2xl mx-auto">
              Stand out from the crowd. Get the checkmark and unlock premium features.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white mb-2">What You Get</h2>
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="flex items-center gap-4 bg-[#16181D] rounded-2xl p-4 border border-[#1E2028]">
                    <div className="h-10 w-10 rounded-full bg-[#FFE500]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-[#FFE500]" />
                    </div>
                    <p className="text-white text-sm">{benefit.text}</p>
                  </div>
                );
              })}

              <div className="bg-[#16181D] rounded-2xl p-6 mt-6 border border-[#1E2028]">
                <h3 className="text-lg font-bold text-white mb-4">Fee Comparison</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[#A0A0A0]">Standard Artist</span>
                    <span className="text-white">15% platform fee</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#FFE500] font-semibold">Verified Artist</span>
                    <span className="text-[#FFE500] font-bold">10% platform fee</span>
                  </div>
                  <p className="text-sm text-[#666] pt-3 border-t border-[#1E2028]">
                    Save 5% on every transaction. The subscription pays for itself!
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="bg-[#FFE500] text-black rounded-3xl p-8 shadow-2xl shadow-[#FFE500]/20">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-extrabold">Verification</h3>
                  <div className="bg-black text-[#FFE500] px-3 py-1 text-xs font-bold uppercase rounded-full">
                    Monthly
                  </div>
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-6xl font-extrabold">₹799</span>
                    <span className="text-black/60">/month</span>
                  </div>
                  <p className="text-black/70 mt-2">Cancel anytime. No long-term commitment.</p>
                </div>

                <div className="space-y-3 mb-6">
                  {benefits.slice(0, 4).map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm">{benefit.text}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleSubscribe}
                  disabled={processing}
                  className="w-full bg-black text-[#FFE500] rounded-full font-bold uppercase tracking-wider py-4 text-base hover:bg-black/80 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
                  data-testid="subscribe-btn"
                >
                  {processing ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Processing...</>
                  ) : (
                    <><CreditCard className="h-5 w-5" /> Subscribe Now</>
                  )}
                </button>

                <p className="text-xs text-center text-black/60 mt-4">
                  Secure payment powered by Razorpay
                </p>
              </div>

              <div className="flex items-center justify-center gap-6 mt-6 text-[#666]">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span className="text-xs">Secure Payment</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-xs">Instant Activation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Verification;
